import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import { ME_KEY, useMe, type AuthUser } from './auth';
import type { UserData } from '@/types/api';

/** The stat tiles across the top, in their standard order. */
export const TILE_IDS = ['mrr', 'open_ar', 'revenue', 'hours', 'clients'] as const;
export type TileId = (typeof TILE_IDS)[number];

/** The cards below the tiles, and the column each one starts in. */
const DEFAULT_COLUMNS = {
  main: ['attention', 'activity'],
  side: ['renewals', 'pipeline', 'quick_actions', 'expiring'],
} as const;
export const CARD_IDS = [...DEFAULT_COLUMNS.main, ...DEFAULT_COLUMNS.side] as const;
export type CardId = (typeof CARD_IDS)[number];
export type Column = 'main' | 'side';

/** A layout with every known tile and card placed exactly once. */
export type DashboardLayout = {
  tiles: TileId[];
  hiddenTiles: TileId[];
  main: CardId[];
  side: CardId[];
  hiddenCards: CardId[];
};

type SavedLayout = NonNullable<UserData['dashboard_layout']>;

export const STANDARD_LAYOUT: DashboardLayout = {
  tiles: [...TILE_IDS],
  hiddenTiles: [],
  main: [...DEFAULT_COLUMNS.main],
  side: [...DEFAULT_COLUMNS.side],
  hiddenCards: [],
};

export function defaultColumnOf(card: CardId): Column {
  return (DEFAULT_COLUMNS.main as readonly string[]).includes(card) ? 'main' : 'side';
}

function known<T extends string>(ids: readonly T[], values: string[] | undefined, taken: Set<string>): T[] {
  const picked: T[] = [];
  for (const value of values ?? []) {
    if ((ids as readonly string[]).includes(value) && !taken.has(value)) {
      taken.add(value);
      picked.push(value as T);
    }
  }
  return picked;
}

/**
 * Turn what's saved into a complete layout. Ids the app no longer has are
 * dropped, and anything added since the layout was saved shows up in its
 * standard place, so a new card is never silently hidden.
 */
export function resolveLayout(saved: SavedLayout | null | undefined): DashboardLayout {
  if (!saved) return STANDARD_LAYOUT;

  const tilesTaken = new Set<string>();
  const tiles = known(TILE_IDS, saved.tiles?.order, tilesTaken);
  const hiddenTiles = known(TILE_IDS, saved.tiles?.hidden, tilesTaken);
  tiles.push(...TILE_IDS.filter((id) => !tilesTaken.has(id)));

  const cardsTaken = new Set<string>();
  const main = known(CARD_IDS, saved.cards?.main, cardsTaken);
  const side = known(CARD_IDS, saved.cards?.side, cardsTaken);
  const hiddenCards = known(CARD_IDS, saved.cards?.hidden, cardsTaken);
  for (const id of CARD_IDS.filter((id) => !cardsTaken.has(id))) {
    (defaultColumnOf(id) === 'main' ? main : side).push(id);
  }

  return { tiles, hiddenTiles, main, side, hiddenCards };
}

function toSaved(layout: DashboardLayout): SavedLayout {
  return {
    tiles: { order: layout.tiles, hidden: layout.hiddenTiles },
    cards: { main: layout.main, side: layout.side, hidden: layout.hiddenCards },
  };
}

/**
 * The signed-in person's dashboard layout, saved to their account so it
 * follows them between devices. Changes show at once and save behind the
 * scenes; a failed save puts the previous layout back.
 */
export function useDashboardLayout() {
  const me = useMe();
  const qc = useQueryClient();
  const saved = me.data?.dashboard_layout;
  // Stable between renders so the arranging view doesn't reset mid-drag every
  // time an unrelated query refreshes the page.
  const layout = useMemo(() => resolveLayout(saved), [saved]);

  const mutation = useMutation({
    mutationFn: async (next: SavedLayout | null): Promise<UserData> => {
      await primeCsrf();
      const { data } = await api.patch<UserData>('/v1/me/preferences', { dashboard_layout: next });
      return data;
    },
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: ME_KEY });
      const previous = qc.getQueryData<AuthUser | null>(ME_KEY);
      if (previous) {
        const updated: AuthUser = { ...previous, dashboard_layout: next };
        qc.setQueryData(ME_KEY, updated);
      }
      return { previous };
    },
    onError: (_error, _next, context) => {
      if (context?.previous) qc.setQueryData(ME_KEY, context.previous);
    },
  });

  return {
    layout,
    save: (next: DashboardLayout) => mutation.mutate(toSaved(next)),
    reset: () => mutation.mutate(null),
    isCustomized: me.data?.dashboard_layout != null,
    saveFailed: mutation.isError,
  };
}
